import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';

import { ThemeProvider } from '@mui/material';
import { LicenseInfo, LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';
import { MutationCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';

import { enableMapSet } from 'immer';
import { ConfirmProvider } from 'material-ui-confirm';

import { AuthProvider } from 'api/authentication/components/AuthProvider';
import { Authenticated } from 'api/authentication/components/Authenticated';
import { showGlobalMutationErrorSnackbar } from 'api/globalMutationErrorSnackbar';
import { App } from 'app/App';
import { AppWithTracking } from 'app/AppWithTracking';
import { HeapContainer } from 'app/HeapContainer';
import { IntercomContainer } from 'app/IntercomContainer';
import { ENV_VARS } from 'app/config/environment-vars';
import 'app/i18n/i18n';
import AppTheme from 'theme/theme';
import { isProduction } from 'utils/env';

import './index.less';
import 'semantic-ui-less/semantic.less';

if (ENV_VARS.MAINTENANCE_MODE_ENABLED) {
  window.location.href = ENV_VARS.MAINTENANCE_MODE_URL;
}

enableMapSet();

LicenseInfo.setLicenseKey(ENV_VARS.MUI_LICENCE_KEY);

const container = document.getElementById('root');

if (!container) {
  throw new Error('Root element not found');
}

const root = createRoot(container);

const queryClient = new QueryClient({
  // Mutations opt into app-wide error feedback with `meta: { showGlobalErrorSnackbar: true }`.
  // See `showGlobalMutationErrorSnackbar` for why this is opt-in and not an app-wide default.
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => showGlobalMutationErrorSnackbar(error, mutation)
  }),
  defaultOptions: {
    queries: {
      // Cache results for 1 minute
      staleTime: 60000,
      retry: (failureCount, error) => {
        // Don't retry if BE returns a known error
        if (error) {
          return false;
        }
        // Only retry up to 2 times for other errors
        return failureCount < 2;
      }
    }
  }
});

root.render(
  <ThemeProvider theme={AppTheme}>
    <ConfirmProvider>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <Authenticated>
            <AppWithTracking>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <BrowserRouter>
                  <App />
                </BrowserRouter>
              </LocalizationProvider>
              {process.env.NODE_ENV !== 'development' && <IntercomContainer />}
              {!isProduction && <HeapContainer />}
            </AppWithTracking>
          </Authenticated>
        </AuthProvider>
      </QueryClientProvider>
    </ConfirmProvider>
  </ThemeProvider>
);
